/**
 * Cookie utility functions for token management
 */

/**
 * Extracts the parent domain from the current URL to share cookies across subdomains
 * @returns Parent domain with leading dot (e.g., '.ideoz.ai') or empty string for localhost
 */
const getParentDomain = (): string => {
    if (typeof window === 'undefined') return '';

    const hostname = window.location.hostname;

    // For localhost or IP addresses, don't set domain
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
        return '';
    }

    // Split hostname by dots
    const parts = hostname.split('.');

    // If it's already a top-level domain (e.g., ideoz.ai), use it with leading dot
    if (parts.length <= 2) {
        return `.${hostname}`;
    }

    // For subdomains (e.g., admin.ideoz.ai), extract parent domain
    const parentDomain = parts.slice(-2).join('.');
    return `.${parentDomain}`;
};

/**
 * Sets a cookie with the specified name, value, and expiration days
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Number of days until expiration (default: 30)
 */
export const setCookie = (name: string, value: string, days: number = 30): void => {
    if (typeof window === 'undefined') return;

    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    const isSecure = window.location.protocol === 'https:';
    const domain = getParentDomain();
    const domainAttribute = domain ? `;domain=${domain}` : '';
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict${isSecure ? ';Secure' : ''}${domainAttribute}`;
};

/**
 * Gets a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
    if (typeof window === 'undefined') return null;

    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
};

/**
 * Removes a cookie by name
 * @param name - Cookie name
 */
export const removeCookie = (name: string): void => {
    if (typeof window === 'undefined') return;

    const domain = getParentDomain();
    const domainAttribute = domain ? `;domain=${domain}` : '';
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainAttribute};`;
};

/**
 * Token-specific cookie utilities
 */
const TOKEN_COOKIE_KEY = 'i_a_t';
const TOKEN_STORAGE_KEY = 'ideoz_access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'ideoz_refresh_token';

/**
 * Saves access token to both cookie and localStorage
 * @param token - Access token to save
 */
export const saveAccessToken = (token: string): void => {
    // Save to cookie
    setCookie(TOKEN_COOKIE_KEY, token, 30);
    // Save to localStorage as backup
    if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
    }
};

/**
 * Saves refresh token to localStorage (not in cookie; used for token refresh)
 * @param token - Refresh token to save
 */
export const saveRefreshToken = (token: string): void => {
    if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
    }
};

/**
 * Gets access token from cookie first, then localStorage as fallback
 * @returns Access token or null if not found
 */
export const getAccessToken = (): string | null => {
    // Try cookie first
    const cookieToken = getCookie(TOKEN_COOKIE_KEY);
    if (cookieToken) {
        return cookieToken;
    }

    // Fallback to localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
        const storageToken = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (storageToken) {
            // If found in localStorage but not in cookie, sync to cookie
            setCookie(TOKEN_COOKIE_KEY, storageToken, 30);
            return storageToken;
        }
    }

    return null;
};

/**
 * Gets refresh token from localStorage
 * @returns Refresh token or null if not found
 */
export const getRefreshToken = (): string | null => {
    if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    }
    return null;
};

/**
 * Removes access token and refresh token from cookie and localStorage (full auth clear)
 */
export const removeAccessToken = (): void => {
    removeCookie(TOKEN_COOKIE_KEY);
    if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }
};

/**
 * Removes only the refresh token (access token unchanged)
 */
export const removeRefreshToken = (): void => {
    if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }
};

